
import { SiteSettings, TopicStructure, AIContentConfig } from '../types';
import * as Gemini from './geminiService';
import * as OpenAI from './openaiService';
import * as DeepSeek from './openaiService'; // DeepSeek e Groq usam a mesma estrutura da OpenAI
import * as Groq from './openaiService';
import { compressBase64, uploadImageToServer } from './imageUtils';
import { searchStockImages } from './stockService';

// --- SMART PROVIDER SELECTION ---
const isProviderActive = (provider: string, settings: SiteSettings) => {
    if (!settings.activeAiProviders || settings.activeAiProviders.length === 0) return true; 
    return settings.activeAiProviders.includes(provider as any);
};

const executeWithFallback = async <T>(
    settings: SiteSettings,
    type: 'text' | 'image',
    tasks: {
        gemini?: () => Promise<T>,
        openai?: () => Promise<T>,
        deepseek?: () => Promise<T>,
        groq?: () => Promise<T>
    },
    preferredProvider?: string
): Promise<T> => {
    const providers: string[] = [];
    
    const hasGoogleKey = !!settings.googleApiKey?.trim() || (settings.googleBackupKeys?.some(k => k?.trim().length > 0) ?? false);
    const hasOpenAIKey = !!settings.openaiApiKey?.trim();
    const hasDeepSeekKey = !!settings.deepseekApiKey?.trim();
    const hasGroqKey = !!settings.groqApiKey?.trim();

    const preferred = preferredProvider || (type === 'text' 
        ? (settings.textAiProvider || settings.aiProvider || 'gemini')
        : (settings.imageAiProvider || settings.aiProvider || 'gemini'));

    const isGoogleActive = isProviderActive('gemini', settings);
    const isOpenAIActive = isProviderActive('openai', settings);
    const isDeepSeekActive = isProviderActive('deepseek', settings);
    const isGroqActive = isProviderActive('groq', settings);

    const canUseGoogle = (isGoogleActive || preferred === 'gemini') && !!tasks.gemini;
    const canUseOpenAI = (isOpenAIActive || preferred === 'openai') && !!tasks.openai;
    const canUseDeepSeek = (isDeepSeekActive || preferred === 'deepseek') && !!tasks.deepseek;
    const canUseGroq = (isGroqActive || preferred === 'groq') && !!tasks.groq;

    // 1. Add preferred if available
    if (preferred === 'gemini' && canUseGoogle) providers.push('gemini');
    if (preferred === 'openai' && canUseOpenAI) providers.push('openai');
    if (preferred === 'deepseek' && canUseDeepSeek) providers.push('deepseek');
    if (preferred === 'groq' && canUseGroq) providers.push('groq');

    // 2. Add fallbacks
    if (canUseGoogle && !providers.includes('gemini')) providers.push('gemini');
    if (canUseOpenAI && !providers.includes('openai')) providers.push('openai');
    if (type === 'text') {
        if (canUseDeepSeek && !providers.includes('deepseek')) providers.push('deepseek');
        if (canUseGroq && !providers.includes('groq')) providers.push('groq');
    }

    if (providers.length === 0) {
        throw new Error(`Nenhum provedor de ${type} ativo e com chave configurada.`);
    }

    let lastError: any;
    let preferredProviderError: any;
    const maxGlobalRetries = 2; // Tenta a lista de provedores 3 vezes no total

    for (let attempt = 0; attempt <= maxGlobalRetries; attempt++) {
        for (const provider of providers) {
            try {
                console.log(`[AI Factory] Tentando provedor: ${provider} para ${type} (Tentativa ${attempt + 1}/${maxGlobalRetries + 1})`);
                if (provider === 'gemini' && tasks.gemini) return await tasks.gemini();
                if (provider === 'openai' && tasks.openai) return await tasks.openai();
                if (provider === 'deepseek' && tasks.deepseek) return await tasks.deepseek();
                if (provider === 'groq' && tasks.groq) return await tasks.groq();
            } catch (error: any) {
                console.warn(`[AI Factory] Provedor ${provider} falhou na tentativa ${attempt + 1}:`, error.message);
                lastError = error;
                if (provider === preferred) {
                    preferredProviderError = error;
                }
            }
        }

        if (attempt < maxGlobalRetries) {
            const delay = Math.pow(2, attempt) * 2000;
            console.warn(`[AI Factory] Todos os provedores falharam. Aguardando ${delay}ms para tentar novamente o ciclo...`);
            await new Promise(r => setTimeout(r, delay));
        }
    }

    const finalErrorMessage = preferredProviderError 
        ? `${preferred.toUpperCase()} falhou: ${preferredProviderError.message}. (Fallbacks também falharam)`
        : `Todos os provedores falharam. Último erro (${providers[providers.length-1]}): ${lastError?.message || 'Erro desconhecido'}`;

    throw new Error(finalErrorMessage);
};

const getGeminiKeys = (settings: SiteSettings) => {
    return [settings.googleApiKey, ...(settings.googleBackupKeys || [])].filter(k => !!k);
};

// --- HELPER: Injeta imagens após H2 ---
const injectImagesIntoHtml = async (html: string, mainKeyword: string, settings: SiteSettings): Promise<string> => {
    // Se não tiver H2, retorna original
    if (!html.includes('<h2')) return html;

    // Encontra todos os H2
    const h2Regex = /<h2.*?>.*?<\/h2>/g;
    const h2Matches = [...html.matchAll(h2Regex)];
    if (h2Matches.length === 0) return html;

    let newHtml = html;
    const maxImages = 3; // Limite de 3 imagens por post para garantir qualidade e relevância
    let imagesAdded = 0;
    
    const source = settings.internalImageSource || 'stock';
    if (source === 'disabled') return html;

    // Distribute images evenly across H2s
    const targetIndices: number[] = [];
    if (h2Matches.length <= maxImages) {
        for (let i = 0; i < h2Matches.length; i++) targetIndices.push(i);
    } else {
        const step = h2Matches.length / maxImages;
        for (let i = 0; i < maxImages; i++) {
            targetIndices.push(Math.floor(i * step));
        }
    }
    // Sort descending to process backwards and not mess up indices
    targetIndices.sort((a, b) => b - a);

    for (const i of targetIndices) {
        if (imagesAdded >= maxImages) break;
        
        const match = h2Matches[i];
        const fullTag = match[0];
        const matchIndex = match.index;
        
        // Extract text content from H2
        const textMatch = fullTag.match(/<h2.*?>(.*?)<\/h2>/);
        if (!textMatch) continue;
        
        let h2Text = textMatch[1].replace(/<[^>]*>/g, '').trim();
        h2Text = h2Text.replace(/^\d+[\.\)\-]\s*/, ''); // Remove "1. ", "2- ", etc
        
        // Extrair as primeiras frases do parágrafo abaixo do H2 para contexto adicional
        let paragraphContext = '';
        if (matchIndex !== undefined) {
            const textAfterH2 = html.substring(matchIndex + fullTag.length, matchIndex + fullTag.length + 1500);
            const pMatch = textAfterH2.match(/<p.*?>(.*?)<\/p>/i);
            if (pMatch) {
                const pText = pMatch[1].replace(/<[^>]*>/g, '').trim();
                const sentences = pText.match(/[^.!?]+[.!?]+/g) || [pText];
                paragraphContext = sentences.slice(0, 2).join(' ').trim();
            }
        }
        
        // Contexto Robusto: Título Principal + Texto do Parágrafo
        const fullContext = `TEMA PRINCIPAL: ${mainKeyword} | CONTEXTO DO TÓPICO: ${paragraphContext}`;
        
        // Se o texto for muito curto, ignora ou usa a palavra chave principal
        const searchQuery = h2Text.length > 3 ? h2Text : mainKeyword;

        try {
            let imageUrl = '';
            let caption = '';

            if (source === 'ai') {
                const visualPrompt = await generateVisualPromptSlogan(h2Text, fullContext, settings);
                const base64 = await generateAIImage(visualPrompt, settings, undefined, 'ai', 'internal');
                // Upload to server to avoid huge HTML and broken images
                imageUrl = await uploadImageToServer(base64, `${searchQuery}-internal.jpg`);
                caption = '';
            } else {
                // Busca 1 imagem específica para este H2
                const stockImages = await searchStockImages(searchQuery, settings, 1);

                if (stockImages && stockImages.length > 0) {
                    const img = stockImages[0];
                    imageUrl = img.url;
                    const providerName = settings.stockImageProvider === 'pixabay' ? 'Pixabay' : 'Pexels';
                    caption = `Foto por <a href="${img.photographer_url}" target="_blank" rel="nofollow" class="hover:underline">${img.photographer}</a> via ${providerName}`;
                }
            }

            if (imageUrl) {
                const cleanAlt = h2Text.replace(/"/g, '&quot;');
                const imageHtml = `
                    <figure class="my-8 w-full">
                        <img src="${imageUrl}" alt="${cleanAlt}" class="w-full h-auto rounded-2xl shadow-lg object-cover max-h-[500px]" loading="lazy" referrerpolicy="no-referrer" />
                        <figcaption class="text-center text-xs text-gray-500 mt-2 italic">${caption}</figcaption>
                    </figure>
                `;
                
                // Replace exactly at the index of the match
                newHtml = newHtml.substring(0, match.index) + fullTag + imageHtml + newHtml.substring(match.index! + fullTag.length);
                imagesAdded++;
            }
        } catch (e) {
            console.error(`Erro ao buscar imagem para H2: ${searchQuery}`, e);
        }
    }

    return newHtml;
};

export const generateAITopicsStructure = async (theme: string, topics: number, subtopics: number, settings: SiteSettings, existingTitles: string[] = []): Promise<TopicStructure[]> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateTopicsStructure(theme, topics, subtopics, getGeminiKeys(settings), existingTitles, siteContext),
        openai: async () => await OpenAI.generateOpenAITopicsStructure(theme, topics, subtopics, settings.openaiApiKey!, existingTitles, siteContext, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAITopicsStructure(theme, topics, subtopics, settings.deepseekApiKey!, existingTitles, siteContext, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAITopicsStructure(theme, topics, subtopics, settings.groqApiKey!, existingTitles, siteContext, 'groq')
    });
};

export const generateAIArticleFromTopics = async (mainTitle: string, category: string, topics: TopicStructure[], settings: SiteSettings): Promise<string> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    let content = await executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateArticleFromTopics(mainTitle, category, topics, settings.blogAiConfig, getGeminiKeys(settings), siteContext),
        openai: async () => await OpenAI.generateOpenAIArticleFromTopics(mainTitle, category, topics, settings.openaiApiKey!, settings.blogAiConfig, siteContext, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIArticleFromTopics(mainTitle, category, topics, settings.deepseekApiKey!, settings.blogAiConfig, siteContext, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIArticleFromTopics(mainTitle, category, topics, settings.groqApiKey!, settings.blogAiConfig, siteContext, 'groq')
    });
    return await injectImagesIntoHtml(content, mainTitle, settings);
};

export const generateAITitlesList = async (topic: string, count: number, settings: SiteSettings, existingTitles: string[] = []): Promise<string[]> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateTitlesList(topic, count, getGeminiKeys(settings), existingTitles),
        openai: async () => await OpenAI.generateOpenAITitlesList(topic, count, settings.openaiApiKey!, existingTitles, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAITitlesList(topic, count, settings.deepseekApiKey!, existingTitles, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAITitlesList(topic, count, settings.groqApiKey!, existingTitles, 'groq')
    });
};

export const generateAINewsExcerpt = async (title: string, settings: SiteSettings): Promise<string> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateNewsExcerpt(title, settings.blogAiConfig, getGeminiKeys(settings)),
        openai: async () => await OpenAI.generateOpenAINewsExcerpt(title, settings.openaiApiKey!, settings.blogAiConfig, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAINewsExcerpt(title, settings.deepseekApiKey!, settings.blogAiConfig, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAINewsExcerpt(title, settings.groqApiKey!, settings.blogAiConfig, 'groq')
    });
};

export const generateAIFullArticle = async (title: string, category: string, settings: SiteSettings): Promise<string> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    let content = await executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateFullArticle(title, category, settings.blogAiConfig, getGeminiKeys(settings), siteContext),
        openai: async () => await OpenAI.generateOpenAIFullArticle(title, category, settings.openaiApiKey!, settings.blogAiConfig, siteContext, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIFullArticle(title, category, settings.deepseekApiKey!, settings.blogAiConfig, siteContext, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIFullArticle(title, category, settings.groqApiKey!, settings.blogAiConfig, siteContext, 'groq')
    });
    return await injectImagesIntoHtml(content, title, settings);
};

export const generateAIRecipeContent = async (title: string, category: string, settings: SiteSettings): Promise<any> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    const recipe = await executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateAIRecipe(title, category, settings.blogAiConfig, getGeminiKeys(settings), siteContext),
        openai: async () => await OpenAI.generateOpenAIRecipe(title, category, settings.openaiApiKey!, settings.blogAiConfig, siteContext, 'openai'),
        deepseek: async () => await DeepSeek.generateOpenAIRecipe(title, category, settings.deepseekApiKey!, settings.blogAiConfig, siteContext, 'deepseek'),
        groq: async () => await Groq.generateOpenAIRecipe(title, category, settings.groqApiKey!, settings.blogAiConfig, siteContext, 'groq')
    });
    
    if (recipe.content) {
        recipe.content = await injectImagesIntoHtml(recipe.content, title, settings);
    }
    
    return recipe;
};

export const generateAIBibleStudyContent = async (title: string, category: string, settings: SiteSettings): Promise<any> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    const study = await executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateBibleStudyContent(title, category, getGeminiKeys(settings), siteContext),
        openai: async () => await OpenAI.generateOpenAIBibleStudy(title, category, settings.openaiApiKey!, settings.blogAiConfig, siteContext, 'openai'),
        deepseek: async () => await DeepSeek.generateOpenAIBibleStudy(title, category, settings.deepseekApiKey!, settings.blogAiConfig, siteContext, 'deepseek'),
        groq: async () => await Groq.generateOpenAIBibleStudy(title, category, settings.groqApiKey!, settings.blogAiConfig, siteContext, 'groq')
    });
    
    if (study.content) {
        study.content = await injectImagesIntoHtml(study.content, title, settings);
    }
    
    return study;
};

export const generateAIInternalLinks = async (content: string, targets: { title: string; url: string; type: 'post' | 'product'; id: string }[], settings: SiteSettings): Promise<any[]> => {
    // Ensure all target URLs are absolute to prevent broken links in subdirectories
    const absoluteTargets = targets.map(t => {
        let absoluteUrl = t.url;
        if (typeof window !== 'undefined' && !absoluteUrl.startsWith('http')) {
            let origin = settings.installationUrl ? settings.installationUrl.replace(/\/$/, '') : window.location.origin;
            // Ensure origin doesn't include /admin
            origin = origin.replace(/\/admin(?:\/.*)?$/, '');
            absoluteUrl = absoluteUrl.startsWith('/') ? `${origin}${absoluteUrl}` : `${origin}/${absoluteUrl}`;
        }
        return { ...t, url: absoluteUrl };
    });

    const suggestions = await executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateInternalLinksJSON(content, absoluteTargets, getGeminiKeys(settings)),
        openai: async () => await OpenAI.generateOpenAIInternalLinks(content, absoluteTargets, settings.openaiApiKey!, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIInternalLinks(content, absoluteTargets, settings.deepseekApiKey!, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIInternalLinks(content, absoluteTargets, settings.groqApiKey!, 'groq')
    });

    // Enforce the exact absolute URL from our targets list, ignoring what the AI might have hallucinated or stripped
    if (Array.isArray(suggestions)) {
        return suggestions.map(sug => {
            const target = absoluteTargets.find(t => String(t.id) === String(sug.artigo_sugerido_id));
            if (target) {
                return { ...sug, targetUrl: target.url };
            }
            return sug;
        });
    }
    
    return suggestions;
};

export const generateAIProductDescription = async (productName: string, settings: SiteSettings): Promise<string> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateProductDescription(productName, getGeminiKeys(settings)),
        openai: async () => await OpenAI.generateOpenAIProductDescription(productName, settings.openaiApiKey!, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIProductDescription(productName, settings.deepseekApiKey!, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIProductDescription(productName, settings.groqApiKey!, 'groq')
    });
};

export const generateAIImage = async (
    prompt: string, 
    settings: SiteSettings, 
    aspectRatio?: string, 
    forceSource?: 'ai' | 'stock',
    imageType?: 'featured' | 'internal' | 'webstory' | 'carousel'
): Promise<string> => {
    const isWebStory = aspectRatio === "9:16" || imageType === 'webstory';
    const source = forceSource || (isWebStory ? settings.webStoryImageSource : (imageType === 'carousel' ? (settings.carouselImageSource || 'ai') : (imageType === 'internal' ? settings.internalImageSource : settings.featuredImageSource))) || 'ai';
    
    if (source === 'disabled') return '';

    // Determine preferred provider based on image type
    let preferredProvider = settings.imageAiProvider;
    if (imageType === 'featured') preferredProvider = settings.featuredImageAiProvider || settings.imageAiProvider;
    else if (imageType === 'internal') preferredProvider = settings.internalImageAiProvider || settings.imageAiProvider;
    else if (imageType === 'webstory') preferredProvider = settings.webStoryImageAiProvider || settings.imageAiProvider;
    else if (imageType === 'carousel') preferredProvider = settings.carouselImageAiProvider || settings.imageAiProvider;

    // Logic for Stock Images
    if (source === 'stock') {
        try {
            // Remove "Scene:" or other prefixes for better search
            const cleanQuery = prompt.replace(/^Scene:\s*/i, '').replace(/^Image of\s*/i, '').substring(0, 50);
            const stockImages = await searchStockImages(cleanQuery, settings, 1);
            if (stockImages && stockImages.length > 0) {
                return stockImages[0].url;
            }
        } catch (e) {
            console.error("Stock Image Fallback Failed, trying AI", e);
        }
    }

    // 2. AI Generation Logic
    let finalPrompt = prompt;
    
    // Only apply Image Brain if NOT using Stock and Image Brain is Active
    if (settings.useImageBrain && settings.imageBrainPrompt) {
        console.log("[AI Factory] Usando Cérebro de Imagem para refinar o prompt...");
        try {
            // Refina o prompt usando Gemini Flash (IA Grátis)
            finalPrompt = await Gemini.enhanceImagePrompt(prompt, settings.imageBrainPrompt, getGeminiKeys(settings));
            console.log("[AI Factory] Prompt refinado:", finalPrompt);
        } catch (e) {
            console.warn("[AI Factory] Falha ao refinar prompt com Cérebro de Imagem, usando fallback...", e);
            // Fallback manual se a IA falhar
            let styleGuide = settings.imageBrainPrompt;
            try {
                const parsed = JSON.parse(settings.imageBrainPrompt);
                if (typeof parsed === 'object') {
                    styleGuide = Object.entries(parsed)
                        .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                        .join('; ');
                }
            } catch (jsonErr) {}
            finalPrompt = `Subject: ${prompt}. Visual Style Guide: ${styleGuide}`;
        }
    }

    let rawImage: string;
    try {
        rawImage = await executeWithFallback(settings, 'image', {
            gemini: async () => {
                if (settings.useImageBrain && settings.imageBrainPrompt) {
                    await new Promise(resolve => setTimeout(resolve, 1500));
                }
                console.log("[AI Factory] Gerando imagem com Gemini...");
                const result = await Gemini.generateImage(finalPrompt, getGeminiKeys(settings), aspectRatio);
                if (!result) throw new Error("Gemini retornou imagem vazia");
                return result;
            },
            openai: async () => {
                if (!settings.openaiApiKey) throw new Error("OpenAI API Key não configurada");
                console.log("[AI Factory] Gerando imagem com OpenAI...");
                return await OpenAI.generateOpenAIImage(finalPrompt, settings.openaiApiKey, aspectRatio);
            }
        }, preferredProvider);
    } catch (error: any) {
        console.warn(`[AI Factory] Todas as IAs de imagem falharam: ${error.message}. Usando Pollinations como fallback final.`);
        
        // Fallback final para Pollinations
        const seed = Math.floor(Math.random() * 1000000);
        const width = aspectRatio === '16:9' ? 1280 : (aspectRatio === '9:16' ? 720 : 1024);
        const height = aspectRatio === '16:9' ? 720 : (aspectRatio === '9:16' ? 1280 : 1024);
        
        rawImage = `https://image.pollinations.ai/prompt/${encodeURIComponent(finalPrompt.substring(0, 500))}?width=${width}&height=${height}&nologo=true&enhance=true&seed=${seed}`;
    }

    console.log("[AI Factory] Imagem gerada, processando...");
    // Para imagens de destaque (featured) ou internas (internal), usamos qualidade fotográfica (0.85) e maior resolução (1200px) para valorizar as APIs pagas (ChatGPT/DALL-E 3)
    const compressionQuality = (imageType === 'featured' || imageType === 'internal') ? 0.85 : 0.75;
    const maxCompressWidth = (imageType === 'featured' || imageType === 'internal') ? 1200 : 1000;
    return await compressBase64(rawImage, maxCompressWidth, compressionQuality);
};

export const generateVisualPromptSlogan = async (title: string, context: string, settings: SiteSettings): Promise<string> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateVisualPromptSlogan(title, context, getGeminiKeys(settings)),
        openai: async () => await OpenAI.generateOpenAIVisualPromptSlogan(title, context, settings.openaiApiKey!, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIVisualPromptSlogan(title, context, settings.deepseekApiKey!, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIVisualPromptSlogan(title, context, settings.groqApiKey!, 'groq')
    });
};

export const generateCarouselSlideVisualPrompt = async (title: string, subtitle: string, context: string, settings: SiteSettings): Promise<string> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateCarouselSlideVisualPrompt(title, subtitle, context, getGeminiKeys(settings)),
        openai: async () => await OpenAI.generateOpenAIVisualPromptSlogan(`${title} - ${subtitle}`, context, settings.openaiApiKey!, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIVisualPromptSlogan(`${title} - ${subtitle}`, context, settings.deepseekApiKey!, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIVisualPromptSlogan(`${title} - ${subtitle}`, context, settings.groqApiKey!, 'groq')
    });
};

export const generateViralTitlesByCategory = async (
    category: string, 
    settings: SiteSettings, 
    count: number = 10, 
    existingTitles: string[] = [],
    brain?: string,
    customTopic?: string
): Promise<string[]> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateViralTitlesByCategory(category, count, getGeminiKeys(settings), existingTitles, brain, customTopic),
        openai: async () => await OpenAI.generateOpenAIViralTitlesByCategory(category, count, settings.openaiApiKey!, existingTitles, 'openai', brain, customTopic),
        deepseek: async () => await OpenAI.generateOpenAIViralTitlesByCategory(category, count, settings.deepseekApiKey!, existingTitles, 'deepseek', brain, customTopic),
        groq: async () => await OpenAI.generateOpenAIViralTitlesByCategory(category, count, settings.groqApiKey!, existingTitles, 'groq', brain, customTopic)
    });
};

export const generateAIViralTitles = async (currentTitle: string, settings: SiteSettings, existingTitles: string[] = []): Promise<string[]> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateViralTitles(currentTitle, getGeminiKeys(settings), existingTitles),
        openai: async () => await OpenAI.generateOpenAIViralTitles(currentTitle, settings.openaiApiKey!, existingTitles, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIViralTitles(currentTitle, settings.deepseekApiKey!, existingTitles, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIViralTitles(currentTitle, settings.groqApiKey!, existingTitles, 'groq')
    });
};

export const generateAIChatResponse = async (history: any[], message: string, context: string, settings: SiteSettings): Promise<string> => {
    const siteTitle = settings.siteTitle || 'este site';
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateChatResponse(history, message, context, getGeminiKeys(settings), siteTitle),
        openai: async () => await OpenAI.generateOpenAIChatResponse(history, message, context, settings.openaiApiKey!, 'openai', siteTitle),
        deepseek: async () => await OpenAI.generateOpenAIChatResponse(history, message, context, settings.deepseekApiKey!, 'deepseek', siteTitle),
        groq: async () => await OpenAI.generateOpenAIChatResponse(history, message, context, settings.groqApiKey!, 'groq', siteTitle)
    });
};

export const rewriteAIContent = async (rawText: string, settings: SiteSettings): Promise<{ title: string; content: string; excerpt: string }> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    let result = await executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.rewriteContent(rawText, settings.blogAiConfig, getGeminiKeys(settings), siteContext),
        openai: async () => await OpenAI.rewriteContent(rawText, settings.openaiApiKey!, settings.blogAiConfig, siteContext, 'openai'),
        deepseek: async () => await OpenAI.rewriteContent(rawText, settings.deepseekApiKey!, settings.blogAiConfig, siteContext, 'deepseek'),
        groq: async () => await OpenAI.rewriteContent(rawText, settings.groqApiKey!, settings.blogAiConfig, siteContext, 'groq')
    });
    result.content = await injectImagesIntoHtml(result.content, result.title, settings);
    return result;
};

export const rewriteRssToUniquePost = async (title: string, content: string, source: string, settings: SiteSettings): Promise<{ title: string; content: string; excerpt: string }> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    let result = await executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.rewriteRssContent(title, content, source, settings.blogAiConfig, getGeminiKeys(settings), siteContext),
        openai: async () => await OpenAI.rewriteContent(`RSS Source: ${source}\nTitle: ${title}\nContent: ${content}`, settings.openaiApiKey!, settings.blogAiConfig, siteContext, 'openai'),
        deepseek: async () => await OpenAI.rewriteContent(`RSS Source: ${source}\nTitle: ${title}\nContent: ${content}`, settings.deepseekApiKey!, settings.blogAiConfig, siteContext, 'deepseek'),
        groq: async () => await OpenAI.rewriteContent(`RSS Source: ${source}\nTitle: ${title}\nContent: ${content}`, settings.groqApiKey!, settings.blogAiConfig, siteContext, 'groq')
    });
    result.content = await injectImagesIntoHtml(result.content, result.title, settings);
    return result;
};

export const generateAILocalSeoArticle = async (service: string, city: string, settings: SiteSettings, whatsapp?: string): Promise<string> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateLocalSeoArticle(service, city, getGeminiKeys(settings), siteContext),
        openai: async () => await OpenAI.generateOpenAILocalSeoArticle(service, city, settings.openaiApiKey!, whatsapp, siteContext, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAILocalSeoArticle(service, city, settings.deepseekApiKey!, whatsapp, siteContext, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAILocalSeoArticle(service, city, settings.groqApiKey!, whatsapp, siteContext, 'groq')
    });
};

export const generateAILocalAuthorityArticles = async (service: string, city: string, settings: SiteSettings, pillarUrl: string): Promise<{ title: string; content: string }[]> => {
    const siteContext = `Site: ${settings.siteTitle || ''} - ${settings.siteDescription || ''}`;
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateLocalAuthorityArticles(service, city, pillarUrl, getGeminiKeys(settings), siteContext),
        openai: async () => await OpenAI.generateOpenAILocalAuthorityArticles(service, city, pillarUrl, settings.openaiApiKey!, siteContext, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAILocalAuthorityArticles(service, city, pillarUrl, settings.deepseekApiKey!, siteContext, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAILocalAuthorityArticles(service, city, pillarUrl, settings.groqApiKey!, siteContext, 'groq')
    });
};

export const generateAIAudio = async (text: string, settings: SiteSettings): Promise<string> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateAudioFromText(text, getGeminiKeys(settings)),
    });
};

export const generateAIReview = async (systemPrompt: string, userPrompt: string, settings: SiteSettings): Promise<string> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateReview(systemPrompt, userPrompt, getGeminiKeys(settings)),
        openai: async () => await OpenAI.generateOpenAIReview(systemPrompt, userPrompt, settings.openaiApiKey!, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIReview(systemPrompt, userPrompt, settings.deepseekApiKey!, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIReview(systemPrompt, userPrompt, settings.groqApiKey!, 'groq')
    });
};

export const generateAIBusinessData = async (prompt: string, settings: SiteSettings): Promise<any> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateBusinessData(prompt, getGeminiKeys(settings)),
        openai: async () => await OpenAI.generateOpenAIBusinessData(prompt, settings.openaiApiKey!, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIBusinessData(prompt, settings.deepseekApiKey!, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIBusinessData(prompt, settings.groqApiKey!, 'groq')
    });
};

export const generateAIWebStory = async (title: string, content: string, settings: SiteSettings): Promise<any> => {
    return executeWithFallback(settings, 'text', {
        gemini: async () => await Gemini.generateWebStoryJSON(title, content, getGeminiKeys(settings)),
        openai: async () => await OpenAI.generateOpenAIWebStory(title, content, settings.openaiApiKey!, 'openai'),
        deepseek: async () => await OpenAI.generateOpenAIWebStory(title, content, settings.deepseekApiKey!, 'deepseek'),
        groq: async () => await OpenAI.generateOpenAIWebStory(title, content, settings.groqApiKey!, 'groq')
    });
};

export const aiFactory = (settings: SiteSettings) => {
    return {
        generateContent: async (prompt: string): Promise<string> => {
            return executeWithFallback(settings, 'text', {
                gemini: async () => await Gemini.generateGeneric(prompt, getGeminiKeys(settings)),
                openai: async () => await OpenAI.generateOpenAIGeneric(prompt, settings.openaiApiKey!, 'openai'),
                deepseek: async () => await OpenAI.generateOpenAIGeneric(prompt, settings.deepseekApiKey!, 'deepseek'),
                groq: async () => await OpenAI.generateOpenAIGeneric(prompt, settings.groqApiKey!, 'groq')
            });
        }
    };
};
